'use client'
import { KeyboardEventHandler, useId } from 'react'

import { mergeProps, normalizeProps, Portal, useMachine } from '@zag-js/react'
import * as select from '@zag-js/select'

import { gt } from '~/lib/gt'

import styles from './Select.module.css'

type ApiSelect = ReturnType<typeof select.connect>
interface Item {
  label: string
  value: string // zag.js value must be a string can't be a object!
  icon?: string
}

interface SelectProps {
  items: Array<Item>
  label?: string
  value?: string
  onChange?: (details: { value: string[] }) => void
  renderOption?: (item: Item) => React.ReactNode
  renderValue?: (api: ApiSelect) => React.ReactNode
  theme?: typeof styles
  onKeyDown?: KeyboardEventHandler<HTMLDivElement> | undefined
}

const defaultRenderOption = (item: Item) => <span>{item.label}</span>
const defaultRenderValue = (api: ApiSelect) => (
  <>
    {api.valueAsString || gt.tp('Select', 'Select option')}
    <div className={styles.icon}>
      <img draggable={false} src={'/lp/assets/icons/arrow.svg'} alt='arrow down' />
    </div>
  </>
)

const Select = ({
  items,
  label,
  value,
  onChange,
  renderOption = defaultRenderOption,
  renderValue = defaultRenderValue,
  onKeyDown,
  theme,
}: SelectProps) => {
  const mergedStyles = { ...styles, ...theme }

  const collection = select.collection({
    items,
  })

  const [state, send] = useMachine(
    select.machine({
      id: useId(),
      collection,
      onValueChange: details => {
        onChange?.(details)
      },
    }),
    {
      context: {
        value: [value?.toString() ?? ''],
        collection,
        onValueChange: details => {
          onChange?.(details)
        },
      },
    }
  )

  const api = select.connect(state, send, normalizeProps)

  return (
    <div {...api.getRootProps()} className={mergedStyles.container} onKeyDown={onKeyDown}>
      <div {...api.getControlProps()} className={mergedStyles.control}>
        {label && (
          <label {...api.getLabelProps()} className={mergedStyles.label}>
            {label}
          </label>
        )}
        <button className={mergedStyles.value} {...api.getTriggerProps()}>
          {renderValue(api)}
        </button>
      </div>

      <Portal>
        <div {...mergeProps(api.getPositionerProps(), { style: { zIndex: 9999 } })}>
          <ul {...api.getContentProps()} className={mergedStyles.content}>
            {items.map(item => (
              <li key={item.value} {...api.getItemProps({ item })} className={mergedStyles.item}>
                {renderOption(item)}
              </li>
            ))}
          </ul>
        </div>
      </Portal>
    </div>
  )
}

export default Select
